import { formatQuantity } from '@nudool/shared';

type LevelGaugeProps = Readonly<{
  levelOnHand: number;
  reorderPoint: number | null;
  orderUpToLevel: number | null;
}>;

function levelText(level: number | null): string {
  return level == null ? 'not set' : (formatQuantity(level) ?? 'not set');
}

function rangeText(reorderPoint: number | null, orderUpToLevel: number | null): string {
  if (reorderPoint != null && orderUpToLevel != null) {
    return `${formatQuantity(reorderPoint)} – ${formatQuantity(orderUpToLevel)}`;
  }
  if (reorderPoint != null) return `Reorder ${formatQuantity(reorderPoint)}`;
  return `Max ${formatQuantity(orderUpToLevel)}`;
}

export function LevelGauge({ levelOnHand, reorderPoint, orderUpToLevel }: LevelGaugeProps) {
  const scale = Math.max(orderUpToLevel ?? (reorderPoint ?? 0) * 2, 1) * 1.25;
  const percent = (value: number) => Math.max(0, Math.min(100, (value / scale) * 100));
  const bandStart = percent(reorderPoint ?? 0);
  const bandWidth = orderUpToLevel == null ? 0 : percent(orderUpToLevel - (reorderPoint ?? 0));
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span
        aria-hidden="true"
        data-testid="level-gauge-track"
        className="relative h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-muted"
      >
        <span
          className="absolute inset-y-0 bg-muted-foreground/25"
          style={{ left: `${bandStart.toFixed(1)}%`, width: `${bandWidth.toFixed(1)}%` }}
        />
        <span
          data-testid="level-gauge-fill"
          className="absolute inset-y-0 left-0 rounded-full bg-foreground/70"
          style={{ width: `${percent(levelOnHand).toFixed(1)}%` }}
        />
        {reorderPoint != null && (
          <span
            data-testid="level-gauge-tick"
            className="absolute inset-y-0 w-0.5 bg-foreground"
            style={{ left: `${bandStart.toFixed(1)}%` }}
          />
        )}
      </span>
      <span className="sr-only">
        {`${formatQuantity(levelOnHand)} on hand, reorder ${levelText(reorderPoint)}, max ${levelText(orderUpToLevel)}`}
      </span>
      <span className="tabular-nums">{rangeText(reorderPoint, orderUpToLevel)}</span>
    </div>
  );
}
